// ABOUTME: React Query mutation hooks for creating, updating, and deleting retarder configurations.
// ABOUTME: Transforms form values to API request shape and invalidates queries on success.

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../../../../App.store'
import { RetarderApiResponse, RetarderFormValues, UpsertRetarderRequest } from '../types/retarder'
import { toCreateScopeAndConfigRequest } from '../utils/pendingScope'
import type { PendingScopeRequest } from '../context/EntityFormContext'

const toApiRequest = (values: RetarderFormValues, uomId: string | undefined): UpsertRetarderRequest => ({
  uom: { isOverridden: true, value: uomId ?? null },
  min: { isOverridden: true, value: values.min },
  max: { isOverridden: true, value: values.max },
  step: { isOverridden: true, value: values.step },
})

export const useCreateRetarder = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values, uomId }: { scopeId: string; values: RetarderFormValues; uomId: string | undefined }) => {
      const res = await api.post<RetarderApiResponse>(
        `/tuc-configs/retarder/${scopeId}`,
        toApiRequest(values, uomId)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['retarderConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'retarder', countryId, businessLine] })
    },
  })
}

export const useCreateRetarderWithScope = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scope, values, uomId }: { scope: PendingScopeRequest; values: RetarderFormValues; uomId: string | undefined }) => {
      const res = await api.post<RetarderApiResponse>(
        '/tuc-configs/retarder',
        toCreateScopeAndConfigRequest(scope, toApiRequest(values, uomId))
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['retarderConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'retarder', countryId, businessLine] })
    },
  })
}

export const useUpdateRetarder = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values, uomId }: { scopeId: string; values: RetarderFormValues; uomId: string | undefined }) => {
      const res = await api.put<RetarderApiResponse>(
        `/tuc-configs/retarder/${scopeId}`,
        toApiRequest(values, uomId)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['retarderConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'retarder', countryId, businessLine] })
    },
  })
}

export const useDeleteRetarder = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (scopeId: string) => {
      await api.delete(`/tuc-configs/retarder/${scopeId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['retarderConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'retarder', countryId, businessLine] })
    },
  })
}
